import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
    CircularProgress,
    Link,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentQuery } from '../../../../../services/query';
import { enumRefundReason, enumRefundStatusType } from '../../../../../types/enums/status';
import Helper from '../../../../../utils/helper';
import TimeHelper from '../../../../../utils/time';

const resolveRefundStatusId = (row: any): number | undefined => {
    if (row.refundStatusId != null) {
        return row.refundStatusId;
    }

    if (!row.refundStatusName) {
        return undefined;
    }

    const statusId = enumRefundStatusType[row.refundStatusName as keyof typeof enumRefundStatusType];
    return typeof statusId === 'number' ? statusId : undefined;
};

const resolveRefundReasonId = (row: any): number | undefined => {
    if (row.refundReasonId != null) {
        return row.refundReasonId;
    }

    if (!row.refundReasonDesc) {
        return undefined;
    }

    const reasonId = enumRefundReason[row.refundReasonDesc as keyof typeof enumRefundReason];
    return typeof reasonId === 'number' ? reasonId : undefined;
};

const getRefundStatusLabel = (row: any): string => {
    const statusId = resolveRefundStatusId(row);
    if (statusId != null) {
        return Helper.getRefundStatusName(statusId) || '-';
    }

    return row.refundStatusName || '-';
};

const getRefundReasonLabel = (row: any): string => {
    const reasonId = resolveRefundReasonId(row);
    if (reasonId != null) {
        return Helper.getRefundReasonName(reasonId) || '-';
    }

    return row.refundReasonDesc || '-';
};

function UserOrderPaymentInfos({ datas, isExpanded }: any) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState<any[]>([]);
    const [refunds, setRefunds] = useState<any[]>([]);
    const [fetchSucceeded, setFetchSucceeded] = useState(false);

    useEffect(() => {
        if (!isExpanded || !datas?.orderId) {
            return;
        }

        setLoading(true);
        setFetchSucceeded(false);

        PaymentQuery.GetOrderPaymentsAndRefunds({
            orderId: datas.orderId,
            orderNumber: datas.orderNumber,
            customerId: datas.customerId,
            refundStatus: -1,
        })
            .then((res: any) => {
                setPayments(res.payments ?? []);
                setRefunds(res.refunds?.items ?? []);
                setFetchSucceeded(true);
            })
            .catch(() => {
                setPayments([]);
                setRefunds([]);
                setFetchSucceeded(false);
            })
            .finally(() => setLoading(false));
    }, [isExpanded, datas?.orderId, datas?.orderNumber, datas?.customerId]);

    const findPaymentMethodName = (row: any) => {
        if (row.paymentMethodId == 3) {
            if (row?.walletPaymentModelData?.walletTypeId == 1) return t('customerInformation.lcwMoney');
            else if (row?.walletPaymentModelData?.walletTypeId == 2) return t('customerInformation.refundMoney');
        } else return row.paymentMethodName;
    };

    const renderEmptyState = (message: string) => (
        <div
            style={{
                color: '#666463',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                marginTop: '1rem',
                marginBottom: '2rem',
            }}
        >
            <HighlightOffIcon style={{ width: '40px', height: 'auto' }} />
            <h2 style={{ margin: 0 }}>{message}</h2>
        </div>
    );

    return (
        <div style={{ padding: '2rem' }}>
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <CircularProgress size={100} />
                </div>
            ) : fetchSucceeded ? (
                <>
                    <Typography className="heading-md" component="strong" sx={{ display: 'block', mb: 2 }}>
                        {t('refund.customerPayments')}
                    </Typography>

                    {payments.length > 0 ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('orderDetail.paymentId')}</TableCell>
                                    <TableCell>{t('orderDetail.orderNumber')}</TableCell>
                                    <TableCell>{t('orderDetail.createDate')}</TableCell>
                                    <TableCell>{t('orderDetail.paymentAmount')}</TableCell>
                                    <TableCell>{t('orderDetail.confirmedAmount')}</TableCell>
                                    <TableCell>{t('orderDetail.paymentMethodName')}</TableCell>
                                    <TableCell>{t('orderDetail.paymentDetails')}</TableCell>
                                    <TableCell>{t('orderDetail.paymentStatusDescription')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((row: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>{row.paymentId}</TableCell>
                                        <TableCell>{row.orderNumber}</TableCell>
                                        <TableCell>{TimeHelper.formatDateHourSecond(row.createDate)}</TableCell>
                                        <TableCell>
                                            {row.paymentAmount?.toFixed(2)} {row.currencySymbol}
                                        </TableCell>
                                        <TableCell>
                                            {row.confirmedAmount?.toFixed(2)} {row.currencySymbol}
                                        </TableCell>
                                        <TableCell>{findPaymentMethodName(row)}</TableCell>
                                        <TableCell>
                                            <Link
                                                style={{ cursor: 'pointer' }}
                                                target={'_blank'}
                                                href={`/customerManagement?ActiveTab=CustomerPaymentLogs&customerId=${datas.customerId}&orderNumber=${row.orderNumber}`}
                                            >
                                                {(() => {
                                                    if (!row.creditCartPaymentModelData) return '';
                                                    const { creditCardTypeName, numberOfInstallments } =
                                                        row.creditCartPaymentModelData;
                                                    const installmentText =
                                                        numberOfInstallments === 0
                                                            ? '-' + t('general.singleInstallment')
                                                            : `-${numberOfInstallments} ${t('general.installments')}`;
                                                    return `${creditCardTypeName} ${installmentText}`;
                                                })()}
                                            </Link>
                                            {row.creditCartPaymentModelData?.loyaltyPointAmount !== undefined &&
                                                row.creditCartPaymentModelData?.loyaltyPointAmount !== null &&
                                                Number(row.creditCartPaymentModelData.loyaltyPointAmount) > 0 && (
                                                    <p style={{ margin: 0, marginTop: '4px', fontWeight: '500' }}>
                                                        {t('orderDetail.loyaltyPointUsed', {
                                                            amount: Number(
                                                                row.creditCartPaymentModelData.loyaltyPointAmount
                                                            ).toFixed(2),
                                                            currency: row.currencySymbol,
                                                        })}
                                                    </p>
                                                )}
                                            {row.creditCartPaymentModelData?.kaspiPaymentId !== undefined &&
                                                row.creditCartPaymentModelData?.kaspiPaymentId !== null &&
                                                row.creditCartPaymentModelData?.kaspiPaymentId !== '0' &&
                                                row.creditCartPaymentModelData?.kaspiPaymentId !== 0 && (
                                                    <p>{row.creditCartPaymentModelData?.kaspiPaymentId}</p>
                                                )}
                                        </TableCell>
                                        <TableCell>{row.paymentStatusDescription}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={8} style={{ width: '100%', textAlign: 'right' }}>
                                        {t('orderDetail.totalRecordsCount')} {payments.length}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    ) : (
                        renderEmptyState(t('orderDetail.paymentListNotFound'))
                    )}

                    <Typography className="heading-md" component="strong" sx={{ display: 'block', mt: 4, mb: 2 }}>
                        {t('orderDetail.customerRefunds')}
                    </Typography>

                    {refunds.length > 0 ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('refundList.refundId')}</TableCell>
                                    <TableCell>{t('orderDetail.orderNumber')}</TableCell>
                                    <TableCell>{t('orderDetail.createDate')}</TableCell>
                                    <TableCell>{t('orderDetail.paymentAmount')}</TableCell>
                                    <TableCell>{t('orderDetail.paymentMethodName')}</TableCell>
                                    <TableCell>{t('refundList.creditCardTypeName')}</TableCell>
                                    <TableCell>{t('refundList.refundReasonDesc')}</TableCell>
                                    <TableCell>{t('refundList.refundStatusName')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {refunds.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Link
                                                href={`/RefundV2/?id=${row.refundId}`}
                                                style={{ cursor: 'pointer' }}
                                                target="_blank"
                                            >
                                                {row.refundId}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{row.orderNumber}</TableCell>
                                        <TableCell>{TimeHelper.formatDateHourSecond(row.createDate)}</TableCell>
                                        <TableCell>
                                            {row.amount?.toFixed(2)} {row.currencySymbol}
                                        </TableCell>
                                        <TableCell>{row.refundMethod}</TableCell>
                                        <TableCell>{row.bankName}</TableCell>
                                        <TableCell>{getRefundReasonLabel(row)}</TableCell>
                                        <TableCell>{getRefundStatusLabel(row)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={8} style={{ width: '100%', textAlign: 'right' }}>
                                        {t('orderDetail.totalRecordsCount')} {refunds.length}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    ) : (
                        renderEmptyState(t('refundList.noRefundFound'))
                    )}
                </>
            ) : null}
        </div>
    );
}

export default UserOrderPaymentInfos;
